import { Component, OnInit } from '@angular/core';
import { OnDestroy } from '@angular/core';
import { NgZone } from '@angular/core';
import { SubscriptionPacketsService } from '../services/subscription-packets.service';
import { SubscriptionPacketDto } from '../../../dtos/subscriptionPacketDto';
import { GlobalDataService } from '../../../shared-services/global-data.service';
import { map, switchMap, tap } from "rxjs/operators";
import { MonumentsDataService } from "../data-storage/monuments-data.service";
import { Observable } from "rxjs";
import { BehaviorSubject } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { SitesDataService } from '../data-storage/sites-data.service';
import { SiteDto } from '../../../dtos/siteDto';
import { UserService } from '../services/user.service';
import { UserDto } from '../../../dtos/userDto';
import { UserDataService } from '../data-storage/user-data.service';
import { PartnerAddressDto } from '../../../dtos/partnerAddressDto';
import { environment } from '../../../../environments/environment';
import { combineLatest } from 'rxjs';

declare var everypay;

@Component({
    selector: 'app-buy-page',
    templateUrl: './buy-page.component.html',
    styleUrls: ['./buy-page.component.scss']
})
export class BuyPageComponent implements OnInit, OnDestroy {

    activeStep = 1;
    isLoginOpen = false;
    packet: SubscriptionPacketDto;
    monumentsMetaCheckboxes$: Observable<any>;
    userForm: FormGroup = new FormGroup({
        basicInfo: new FormControl(""),
        billingInfo: new FormControl("")
    });
    siteSelected$: Observable<SiteDto> | Observable<SiteDto[]>;
    packetsId;
    flowLoading: boolean = false;
    acceptError: boolean = false;
    currentUser: UserDto;

    constructor(private subscriptionPacketsService: SubscriptionPacketsService,
                private monumentsDataService: MonumentsDataService,
                private sitesDataService: SitesDataService,
                public globalDataService: GlobalDataService,
                private userDataService: UserDataService,
                private userService: UserService,
                private zone: NgZone) {
    }

    ngOnInit(): void {
        this.packetsId = sessionStorage.getItem("packetId").split(",");
        this.currentUser = this.userDataService.getUser();
        if(this.packetsId.length === 1){
            this.singleBuy(this.packetsId[0])
        }else if(this.packetsId.length > 1){
            this.multiBuy();
        }
    }

    ngOnDestroy(): void {
        sessionStorage.removeItem("packetId");
    }

    goBilling() {
        if (this.userForm.valid) {
            const subscription = this.updateUserInfo()
                .subscribe(() => {
                    this.activeStep = 2;
                    this.initPayForm(this.packet);
                    setTimeout(() => {
                        everypay.showForm();
                        subscription.unsubscribe();
                    })
                });
            this.flowLoading = true;
        }
    }

    payButton(checkBox) {
        if(checkBox.checked){
            this.flowLoading = true;
            everypay.onClick();
        }else{
            this.acceptError = true;
        }
    }

    checkTerms(checkBox){
        this.acceptError = !checkBox.checked;
    }

    private singleBuy(packetId){
        this.monumentsMetaCheckboxes$ = this.subscriptionPacketsService.getSinglePacket(packetId).pipe(
            tap((packet: SubscriptionPacketDto) => {
                this.packet = packet;
                this.siteSelected$ = this.sitesDataService.getSiteById(packet.siteId);
            }),
            switchMap((packet: SubscriptionPacketDto) => this.monumentsDataService.getMonumentBySiteId(packet.siteId)),
            map((monuments) => {
                return {
                    length: monuments.length,
                    panoramics: monuments.filter((monument) => monument.componentConfiguration.vr).length,
                    models: monuments.filter((monument) => monument.componentConfiguration.model).length,
                    info: monuments.filter((monument) => monument.componentConfiguration.info).length,
                }
            })
        )
    }

    private multiBuy(){
        this.siteSelected$ = new BehaviorSubject(new SiteDto());
        const packetsObservables$ = [];
        for(let packetId of this.packetsId){
            packetsObservables$.push(this.subscriptionPacketsService.getSinglePacket(packetId))
        }
        this.monumentsMetaCheckboxes$ = combineLatest(packetsObservables$).pipe(
            tap((packets:SubscriptionPacketDto[])=>{
                let combinedPacket = new SubscriptionPacketDto();
                combinedPacket.price = this.subscriptionPacketsService.getTotalPrice(packets,true);
                combinedPacket.id = packets.map((el)=>el.id);
                combinedPacket.effectiveDays = packets[0].effectiveDays;
                combinedPacket.discount = this.subscriptionPacketsService.getTotalPrice(packets) - combinedPacket.price;
                this.packet = combinedPacket;
                this.siteSelected$ = combineLatest(packets
                    .map((packet)=>packet.siteId)
                    .map( (id)=> this.sitesDataService.getSiteById(id)));
            }),
            switchMap((packets:SubscriptionPacketDto[]) => {
                const combinedObs = [];
                for(let packet of packets){
                    combinedObs.push(this.monumentsDataService.getMonumentBySiteId(packet.siteId))
                }
                return combineLatest(combinedObs);
            }),
            map((monuments: Array<any>) => {
                const allMonuments = monuments.reduce((acc, val) => acc.concat(val), []);
                return {
                    length: allMonuments.length,
                    panoramics: allMonuments.filter((monument) => monument.componentConfiguration.vr).length,
                    models: allMonuments.filter((monument) => monument.componentConfiguration.model).length,
                    info: allMonuments.filter((monument) => monument.componentConfiguration.info).length,
                };
            })
        )
    }

    private initPayForm(packet: SubscriptionPacketDto) {
        let payload = {
            pk: environment.everypay.pk,
            amount: Math.round(Number(packet.price * 100)),
            locale: this.globalDataService.getCurrentLang(),
            txnType: 'tds',
            display: {
                button: false
            },
            formOptions: {
                size: 'lg',
            }
        }
        everypay.payform(payload, this.handleResponse.bind(this));
    }


    private handleResponse(data) {
        if (data.response && data.response === 'success') {
            const packetIds =  Array.isArray(this.packet.id) ? this.packet.id.join(',') : this.packet.id;
            let subscription = this.subscriptionPacketsService.buyPacket({token: data.token, packet: packetIds})
                .subscribe(() => {
                    this.zone.run(() => {
                        this.flowLoading = false;
                        this.activeStep = 3;
                        if(Array.isArray(this.packet.id)){
                            // @ts-ignore
                            this.currentUser.sitePurchased.push(...this.packet.siteId);
                        }else{
                            this.currentUser.sitePurchased.push(this.packet.siteId);
                        }

                        sessionStorage.removeItem("packetId")
                    })
                    subscription.unsubscribe();
                }, () => {
                    this.flowLoading = false;
                    alert("Something went wrong with the payment process please contact the administrator of the page.")
                })
        } else {
            this.zone.run(() => {
                this.flowLoading = false;
            })
            everypay.showForm();
        }
    }

    private updateUserInfo() {

        if (this.userForm.value.basicInfo !== "") {
            this.currentUser.email = this.userForm.value.basicInfo.email;
            this.currentUser.name = this.userForm.value.basicInfo.name;
            this.currentUser.lastName = this.userForm.value.basicInfo.lastName;
        }
        if (this.userForm.value.basicInfo !== "") {
            const userAddress: PartnerAddressDto = this.currentUser.addresses.find((el) => el.primary);
            userAddress.phone = this.userForm.value.basicInfo.phone;
            userAddress.street = this.userForm.value.billingInfo.street;
            userAddress.country = this.userForm.value.billingInfo.country;
            userAddress.postalCode = Number(this.userForm.value.billingInfo.postcode);
            userAddress.area = this.userForm.value.billingInfo.region;
        }
        return this.userForm.pristine ? new BehaviorSubject({}).asObservable() : this.userService.updateMe(this.currentUser);
    }

}
